import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { hideSlideOutPanel, modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { BaseCompAction, Time, TimeSeriesType, BaseInfoCompProps, BaseCompSubAction, capitalizeAll } from '@moderepo/mode-app-components';
import { useNavigate } from 'react-router-dom';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { FetchRawSimpleSeriesDataParams, FetchAggregatedSimpleSeriesDataParams, FetchRawCollectionDataParams, FetchAggregatedCollectionDataParams,
    isSimpleSeriesDashboardWidgetDataSource, WidgetGroupWidgetSettings, isCollectionDashboardWidgetDataSource, isEntityDashboardWidgetDataSource,
    HeatMapDashboardWidgetSettings, ValueDashboardWidgetSettings, DataIntervalDashboardWidgetSettings, isDataIntervalDashboardWidgetSettings,
    isHeatMapDashboardWidgetSettings, isValueDashboardWidgetSettings } from './dashboardTypes';
import { useRitzTranslation } from '../../utils/customHooks';
import { DashboardWidgetPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { fetchTSData, selectTSData, TSDashboardWidgetRequiredViewData } from './dashboardLogic';
import { RouteHelper } from '../routes/RouteHelper';
import { createDashboardPrimaryWidgetActions, createDashboardSecondaryWidgetActions, useWidgetDataSource } from './dashboardUtils';
import { useSelectEntity } from '../../utils/dataStateHooks';
import { getMetricsDefinitionMetricsId } from '../entities';
import { DataIntervalDashboardWidgetDataViewContainer } from './DataIntervalDashboardWidgetDataViewContainer';
import { HeatMapDashboardWidgetDataViewContainer } from './HeatMapDashboardWidgetDataViewContainer';
import { ValueDashboardWidgetDataViewContainer } from './ValueDashboardWidgetDataViewContainer';
import { WidgetDataViewPlaceholderComp } from '../../components';
import { useSelectTranslatedEntityClasses } from '..';


const useStyle = makeStyles(() => {
    return {
        loadingContainer: {
            position      : 'absolute',
            width         : '100%',
            height        : '100%',
            zIndex        : 1,
            top           : 0,
            left          : 0,
            background    : 'rgba(0, 0, 0, 0.3)',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            color         : '#ffffff',
        },
        placeholderWidget: {
            width : '100%',
            height: '100%',
        },
    };
}, { name: 'TSDBDashboardWidgetDataViewContainer', index: 2 });



export interface TSDashboardWidgetDataViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;
    readonly selectedWidget: DataIntervalDashboardWidgetSettings | ValueDashboardWidgetSettings | HeatMapDashboardWidgetSettings;

    // An array of widgets of type WidgetGroup. This will be used to display the list of widget group this widget can be moved into
    readonly widgetGroups: readonly WidgetGroupWidgetSettings[];
    readonly isInMoveAndResizeMode?: boolean | undefined;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;


    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }

    // The handler for when the widget itself wants to enable move and resize mode. The widget itself doesn't control the mode so it will
    // call the handler to handle the event
    readonly enableMoveAndResizeModeHandler?: ()=> void;
}



/**
 * This is the WRAPPER component for displaying the data for TSDB dashboard widgets, DataInterval, Value, HeatMap, etc...
 * This component will take care of fetching data and handle all the actions but it does not have a UI. Once the data is loaded, it
 * will pass this data to the specific component to display
 */
export const TSDashboardWidgetDataViewContainer: React.FC<TSDashboardWidgetDataViewContainerProps> = (
    props: TSDashboardWidgetDataViewContainerProps,
) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditDashboard = projectAgent?.role === AgentRole.ADMIN;

    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // For optimization, use the 'useWidgetDataSource' hook to get the dataSource. This hook will make sure this object
    // doesn't change unless the content of the dataSource changed.
    const dataSource = useWidgetDataSource(props.selectedWidget.dataSource);

    // Select all entity classes. We might need it if the data source is Entity
    // Select the entity instance and metricsDefinition object if the widget's data source is ENTITY
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntity = useSelectEntity(
        props.projectId,
        isEntityDashboardWidgetDataSource(dataSource) ? dataSource.entityId : undefined,
        false,
    );
    const selectedMetricsDefinition = useMemo(() => {
        if (isEntityDashboardWidgetDataSource(dataSource) && selectedEntity && entityClasses) {
            // Find the entityClass that the selectedEntity belonged to
            const entityClass = entityClasses.find((entClass) => {
                return entClass.entityClass === selectedEntity.entityClass;
            });

            if (entityClass) {
                return entityClass.metricsDefinitions?.find((definition) => {
                    return definition.metricsDefinitionId === dataSource.metricsDefinitionId;
                });
            }
        }
        return undefined;
    }, [dataSource, entityClasses, selectedEntity]);


    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const routeHelper = RouteHelper.getInstance(navigate);


    // After we dispatched an action to fetch data, we need to select the data from the data state. To select the data, we need to use
    // the same params we used when fetching data. Therefore, we need this state to remember what params was used when fetching data.
    const [fetchDataParams, setFetchDataParams] = useState<
    FetchAggregatedSimpleSeriesDataParams | FetchRawSimpleSeriesDataParams
    | FetchAggregatedCollectionDataParams | FetchRawCollectionDataParams | undefined
    >();

    // The metrics' data that we have fetched and ready for display
    const [metricsData, setMetricData] = useState<TSDashboardWidgetRequiredViewData | undefined>();

    // A flag to track if we have fetched data for this widget at least once. When in mode/resize mode, we usually don't auto fetch
    // data BUT if we have never fetched data before, we will need to fetch data.
    const dataFetchedOnce = useRef<boolean>(false);


    /**
     * Just a helper function to reload data
     */
    const handleFetchTSData = useCallback(() => {

        // If widget's data source is ENTITY, fetchTSData will need 'selectedEntity' and 'selectedMetricsDefinition` objects.
        // If these data are not loaded then the function will return 'undefined' and we will need to try again. However, it is
        // better that we don't call this function at all if the required data is NOT ready so we will add a check 'isEntityInfoReady'
        // To prevent from calling 'fetchTSData'
        // Entity info is ready if the widget's dataSource IS NOT Entity type or it is Entity type and the selectedEntity
        // and selectedMetricsDefinition objects are loaded
        const isEntityInfoReady = !isEntityDashboardWidgetDataSource(dataSource) || (selectedEntity && selectedMetricsDefinition);

        // For optimization, don't reload data if we are in editing mode OR if we have not fetched data at least once
        if ((!props.isInMoveAndResizeMode || dataFetchedOnce.current === false) && isEntityInfoReady) {
            // Update the 'dataFetchedOnce' flag right away instead of waiting for the fetchTSData to finish
            dataFetchedOnce.current = true;
            setIsLoading(true);

            // Call the main fetchData function, it will take care of calling the correct fetch function based on the widget settings
            // and returns the fetch params that was used. Save the fetchParams so that we can use it later to select data
            fetchTSData(appDataState, appDataDispatch, dataSource.homeId, dataSource, selectedEntity, selectedMetricsDefinition)
                .then((fetchParams) => {
                    if (fetchParams) {
                        setFetchDataParams(fetchParams);
                    }
                    setIsLoading(false);
                });
        }

    // NOTE: This function uses 'appDataState` but it can't have 'appDataState' in the dependencies.
    // This function will make API call to fetch data which causes the appDataState to change therefore causes this function to be
    // called again if it depends on 'appDataState'. Therefore, we need to exclude 'appDataState' from the list of dependencies
    // and we need the next line to disable warning from ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appDataDispatch, dataSource, props.isInMoveAndResizeMode, selectedEntity, selectedMetricsDefinition]);



    /**
     * This useEffect is used for fetching data on component init and when the local 'widget' is updated
     */
    useEffect(() => {

        // Call fetch data once on component init.
        handleFetchTSData();

        // Create an interval to call handleFetchTSData again once every reloadDataFreqInMins but only if reloadDataFreqInMins is greater than 0
        const intervalId = props.selectedWidget.reloadDataFreqInMins
            ? window.setInterval(() => {
                handleFetchTSData();
            }, props.selectedWidget.reloadDataFreqInMins * Time.MINUTE_IN_MS) // reloadDataFreqInMins is in MINUTES so need to convert it to MS
            : undefined;


        // Return a cleanup function that will stop the interval
        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [handleFetchTSData, props.selectedWidget.reloadDataFreqInMins]);



    /**
     * Select the data we fetched
     */
    useEffect(() => {
        // if there is a fetchDataParams state then that mean we just created action to fetch params. We can now call selectData to get
        // data from the data state
        if (fetchDataParams) {
            const data = selectTSData(appDataState, dataSource, fetchDataParams, selectedEntity, selectedMetricsDefinition);
            if (data) {
                // we got the data, we can now clear the fetchDataParams so that we don't select data again
                setFetchDataParams(undefined);
                setMetricData(data);
            }
        }
    }, [appDataState, dataSource, fetchDataParams, selectedEntity, selectedMetricsDefinition]);



    /**
     * On Show Dashboard Widget settings button clicked, we will show the dashboard widget's settings in a Slide-out panel
     */
    const showDashboardWidgetPreview = useCallback(() => {
        const options: DashboardWidgetPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_WIDGET_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId     : props.projectId,
                    dashboardId   : props.dashboardId,
                    selectedWidget: props.selectedWidget.id,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.routeContext, props.dashboardId, props.projectId, props.selectedWidget.id, uiDispatch]);



    /**
     * On 'View Historical Data', take the user to the TSDB historical data page
     */
    const onViewHistoricalData = useCallback(() => {
        if (isSimpleSeriesDashboardWidgetDataSource(dataSource)) {
            routeHelper.gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                userId           : props.routeContext?.userId,
                homeId           : dataSource.homeId,
                tsdbSmartModuleId: dataSource.smartModuleId,
                dataId           : dataSource.seriesIds.join(','),
                timeSeriesType   : TimeSeriesType.SIMPLE,
            });
            uiDispatch(hideSlideOutPanel());
        } else if (isCollectionDashboardWidgetDataSource(dataSource)) {
            routeHelper.gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                userId           : props.routeContext?.userId,
                homeId           : dataSource.homeId,
                tsdbSmartModuleId: dataSource.smartModuleId,
                dataId           : dataSource.collectionId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
            });
            uiDispatch(hideSlideOutPanel());
        } else if (isEntityDashboardWidgetDataSource(dataSource) && selectedEntity && selectedMetricsDefinition) {
            routeHelper.gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                userId           : props.routeContext?.userId,
                homeId           : dataSource.homeId,
                tsdbSmartModuleId: selectedMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                dataId           : getMetricsDefinitionMetricsId(selectedEntity, selectedMetricsDefinition.metricsDefinitionId),
                timeSeriesType   : TimeSeriesType.COLLECTION,
                metricsDefinition: {
                    entityClass        : selectedEntity.entityClass,
                    metricsDefinitionId: selectedMetricsDefinition.metricsDefinitionId,
                },
            });
            uiDispatch(hideSlideOutPanel());
        }
    }, [dataSource, selectedEntity, selectedMetricsDefinition, routeHelper, props.projectId, props.routeContext, uiDispatch]);



    /**
     * On 'View Entity Details' action clicked, take the user to the entity details page,
     */
    const onViewEntityDetails = useCallback(() => {
        if (isEntityDashboardWidgetDataSource(dataSource)) {
            routeHelper.gotoEntityDetailsPage(props.projectId, dataSource.entityId);
        }
    }, [dataSource, props.projectId, routeHelper]);


    /**
     * Create the PRIMARY Actions to show in the widget's panel header, the top right corner.
     */
    const widgetPrimaryActions = useMemo((): readonly BaseCompAction[] | undefined => {
        if (!metricsData) {
            return undefined;
        }

        return createDashboardPrimaryWidgetActions({
            isInMoveAndResizeMode: Boolean(props.isInMoveAndResizeMode),
            trans,
            viewDataHandler      : isEntityDashboardWidgetDataSource(dataSource) ? onViewEntityDetails : onViewHistoricalData,
        });
    }, [dataSource, metricsData, onViewEntityDetails, onViewHistoricalData, props.isInMoveAndResizeMode, trans]);


    /**
     * Create the SECONDARY Actions to show in the widget's panel header, the top right corner.
     */
    const widgetSecondaryActions = useMemo((): readonly (BaseCompAction | BaseCompSubAction)[] | undefined => {
        if (!canEditDashboard) {
            return undefined;
        }

        if (!props.isInMoveAndResizeMode && !metricsData) {
            // If data is not loaded, only enable the "Settings" and "Move and Resize" options.
            const actions: (BaseCompAction | BaseCompSubAction)[] = [{
                icon   : 'settings',
                label  : capitalizeAll(trans('label.settings')),
                onClick: showDashboardWidgetPreview,
            }];
            if (props.enableMoveAndResizeModeHandler) {
                actions.push({
                    icon   : 'open_with',
                    label  : capitalizeAll(trans('dashboard.action.move_and_resize_widgets')),
                    onClick: props.enableMoveAndResizeModeHandler,
                });
            }
            return actions;
        }

        return createDashboardSecondaryWidgetActions({
            targetWidget                     : props.selectedWidget,
            widgetGroupWidgets               : props.widgetGroups,
            isInMoveAndResizeMode            : Boolean(props.isInMoveAndResizeMode),
            uiDispatch,
            dataState,
            dataDispatch,
            appDataState,
            appDataDispatch,
            loggedInAgent,
            projectId                        : props.projectId,
            homeId                           : props.routeContext?.homeId,
            dashboardId                      : props.dashboardId,
            trans,
            showDashboardWidgetPreviewHandler: showDashboardWidgetPreview,
            fetchDataHandler                 : handleFetchTSData,
            enableMoveAndResizeModeHandler   : props.enableMoveAndResizeModeHandler,
        });
    }, [appDataDispatch, appDataState, canEditDashboard, dataDispatch, dataState, handleFetchTSData, loggedInAgent, props.dashboardId,
        props.isInMoveAndResizeMode, props.projectId, props.routeContext, props.selectedWidget, props.widgetGroups, showDashboardWidgetPreview,
        trans, uiDispatch, props.enableMoveAndResizeModeHandler, metricsData]);


    /**
     * Create and return the component that can display the selected widget data based on the widget type
     */
    const createDataViewComponent = useCallback((data: TSDashboardWidgetRequiredViewData) => {
        if (isDataIntervalDashboardWidgetSettings(props.selectedWidget)) {
            return (
                <DataIntervalDashboardWidgetDataViewContainer
                    projectId={props.projectId}
                    infoCompProps={props.infoCompProps}
                    selectedWidget={props.selectedWidget}
                    metricsData={data}
                    widgetPrimaryActions={widgetPrimaryActions}
                    widgetSecondaryActions={widgetSecondaryActions}
                />
            );
        }
        if (isValueDashboardWidgetSettings(props.selectedWidget)) {
            return (
                <ValueDashboardWidgetDataViewContainer
                    projectId={props.projectId}
                    infoCompProps={props.infoCompProps}
                    selectedWidget={props.selectedWidget}
                    metricsData={data}
                    widgetPrimaryActions={widgetPrimaryActions}
                    widgetSecondaryActions={widgetSecondaryActions}
                />
            );
        }
        if (isHeatMapDashboardWidgetSettings(props.selectedWidget)) {
            return (
                <HeatMapDashboardWidgetDataViewContainer
                    projectId={props.projectId}
                    infoCompProps={props.infoCompProps}
                    selectedWidget={props.selectedWidget}
                    metricsData={data}
                    widgetPrimaryActions={widgetPrimaryActions}
                    widgetSecondaryActions={widgetSecondaryActions}
                />
            );
        }

        // It shouldn't get to here
        // eslint-disable-next-line no-console
        console.error('Unsupported widget type', props.selectedWidget);
        return (
            <></>
        );
    }, [props.selectedWidget, props.projectId, props.infoCompProps, widgetPrimaryActions, widgetSecondaryActions]);


    return (
        <>
            {metricsData && (metricsData.numericMetricsData || metricsData.tagMetricsData) ? (
                <>
                    {createDataViewComponent(metricsData)}
                    {isLoading && (
                        <div className={classes.loadingContainer}>
                            <CircularProgress color="inherit" />
                        </div>
                    )}
                </>
            ) : (
                <WidgetDataViewPlaceholderComp
                    className={clsx(classes.placeholderWidget, props.infoCompProps?.className)}
                    title={props.selectedWidget.title}
                    icon={props.selectedWidget.icon}
                    customPrimaryActions={widgetPrimaryActions}
                    customSecondaryActions={widgetSecondaryActions}
                    showCustomActionOnHover
                />
            )}
        </>
    );
};
